import { EventEmitter, Inject, Injectable } from '@angular/core';
import { lastValueFrom, map, Observable, of, shareReplay, tap } from 'rxjs';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { HttpClient } from '@angular/common/http';
import { SharedParametrizationsModel } from '../../helpers/objects/shared-parametrizations/shared-parametrizations-model';
import { PlatformEnum } from '@fh-client-interaction-frontends/fh-enums';

@Injectable({
  providedIn: 'root'
})
export class SharedFeaturesService {
  features: SharedParametrizationsModel[] = new Array<SharedParametrizationsModel>();
  appConfig: EnvironmentModel = new EnvironmentModel();
  updated: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(@Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel, private http: HttpClient) {
    this.appConfig = appConfigTemp;
  }

  fetchParametrizations(token?: string): Observable<SharedParametrizationsModel[]> {
    if (this.features.length > 0) {
      return of(this.features);
    } else {
      let url: string = '';

      switch (this.appConfig.platform) {
        case PlatformEnum.salesPortal:
          url = this.appConfig.api + 'setup/menus/' + this.appConfig.apiPartnershipId;
          break;
        case PlatformEnum.clientPortal:
          if (token) {
            url = this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/parametrizations';
          } else {
            url = '';
          }
          break;
        default:
          url = '';
          break;
      }

      if (url == '') {
        return of(new Array<SharedParametrizationsModel>());
      }

      return this.http.get<any>(url).pipe(
        map((response) => response.body.features),
        tap((response) => (this.features = response)),
        shareReplay(1)
      );
    }
  }

  resetParametrizations(token?: string): void {
    this.features = [];
    if (token) {
      this.fetchParametrizations(token).subscribe({
        next: () => this.updated.emit(true)
      });
    }
  }

  public async get(featureParent: string, feature?: string, token?: string) {
    const features: SharedParametrizationsModel[] = await lastValueFrom(this.fetchParametrizations(token));

    if (features == undefined) {
      return new Array<SharedParametrizationsModel>();
    }

    if (feature) {
      return features.filter(
        (row: SharedParametrizationsModel) =>
          row.parentFeatureName == featureParent && row.features.filter((featureRow) => featureRow.featureName == feature).length > 0
      );
    } else {
      return features.filter((row: SharedParametrizationsModel) => row.parentFeatureName == featureParent);
    }
  }

  public hasUserFeature(feature: string): boolean {
    const raw = sessionStorage.getItem('features');
    if (!raw) {
      return false;
    }
    try {
      const features: string[] = JSON.parse(raw);
      return Array.isArray(features) && features.includes(feature);
    } catch {
      return false;
    }
  }

  public async exists(feature: string, featureParent?: string, token?: string) {
    const features: SharedParametrizationsModel[] = await lastValueFrom(this.fetchParametrizations(token));

    if (feature == '' && featureParent == '') {
      return true;
    }

    if (features == undefined) {
      return false;
    }

    if (!!(featureParent && feature)) {
      return (
        features.filter(
          (row: SharedParametrizationsModel) =>
            row.parentFeatureName == featureParent && row.features.filter((featureRow) => featureRow.featureName == feature).length > 0
        ).length > 0
      );
    } else {
      return features.filter((row: SharedParametrizationsModel) => row.parentFeatureName == feature).length > 0;
    }
  }
}
